import { useId, type ReactNode } from "react";
// The schematic owns its ink: calc-schematic__* rules live in schema-calc.css,
// and consumers OUTSIDE the schema sheet (the export document's print pages,
// the QA gallery) must not depend on SchemaCalcSheet's chunk having loaded —
// unstyled SVG renders default-black there otherwise. Token fallbacks in the
// rules keep the spruce/ink ramp correct even without the .nds-workspace scope.
import "../../styles/schema-calc.css";

export type CalculatorSchematicValues = Record<string, unknown>;

type Props = {
  calculatorId: string;
  values: CalculatorSchematicValues;
  className?: string;
};

function numberValue(values: CalculatorSchematicValues, key: string, fallback = 0) {
  const value = Number(values[key]);
  return Number.isFinite(value) ? value : fallback;
}

function textValue(values: CalculatorSchematicValues, key: string, fallback: string) {
  const value = values[key];
  return value == null || value === "" ? fallback : String(value);
}

function valueLabel(value: number, unit: string) {
  return `${new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 }).format(value)} ${unit}`;
}

function SvgFrame({
  title,
  subtitle,
  className,
  children,
}: {
  title: string;
  subtitle: string;
  className?: string;
  children: ReactNode;
}) {
  return (
    <figure className={["calc-schematic-card", className].filter(Boolean).join(" ")}>
      <svg
        className="calc-schematic"
        viewBox="0 0 420 230"
        role="img"
        aria-label={`${title}. ${subtitle}`}
      >
        {children}
      </svg>
      <figcaption>
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </figcaption>
    </figure>
  );
}

function ArrowDefs({ id }: { id: string }) {
  return (
    <defs>
      <marker id={id} markerWidth="8" markerHeight="8" refX="6" refY="4" orient="auto">
        <path d="M0,0 L8,4 L0,8 z" className="calc-schematic__arrow-head" />
      </marker>
      <pattern id={`${id}-masonry`} width="18" height="10" patternUnits="userSpaceOnUse">
        <path d="M0 0h18M0 10h18M9 0v10" className="calc-schematic__hatch" />
      </pattern>
    </defs>
  );
}

function Dimension({
  x1,
  y1,
  x2,
  y2,
  label,
}: {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  label: string;
}) {
  const vertical = x1 === x2;
  return (
    <g className="calc-schematic__dimension">
      <line x1={x1} y1={y1} x2={x2} y2={y2} />
      {vertical ? (
        <>
          <line x1={x1 - 5} y1={y1} x2={x1 + 5} y2={y1} />
          <line x1={x2 - 5} y1={y2} x2={x2 + 5} y2={y2} />
          <text x={x1 + 8} y={(y1 + y2) / 2} dominantBaseline="middle">{label}</text>
        </>
      ) : (
        <>
          <line x1={x1} y1={y1 - 5} x2={x1} y2={y1 + 5} />
          <line x1={x2} y1={y2 - 5} x2={x2} y2={y2 + 5} />
          <text x={(x1 + x2) / 2} y={y1 - 7} textAnchor="middle">{label}</text>
        </>
      )}
    </g>
  );
}

function Support({ x, y }: { x: number; y: number }) {
  return <path d={`M${x} ${y} l-11 16 h22 z`} className="calc-schematic__support" />;
}

function UniformLoad({
  marker,
  y = 42,
  start = 58,
  end = 318,
  count = 7,
}: {
  marker: string;
  y?: number;
  start?: number;
  end?: number;
  count?: number;
}) {
  return (
    <g className="calc-schematic__load">
      <line x1={start} y1={y} x2={end} y2={y} />
      {Array.from({ length: count }, (_, index) => {
        const x = start + (end - start) * index / Math.max(count - 1, 1);
        return <line key={x} x1={x} y1={y} x2={x} y2={y + 42} markerEnd={`url(#${marker})`} />;
      })}
    </g>
  );
}

function BeamSchematic({ calculatorId, values, marker }: Props & { marker: string }) {
  const span = numberValue(values, calculatorId === "masonry_lintel_tms" ? "effective_span_ft" : "span_ft", 6);
  const dead = numberValue(values, "dead_load_plf");
  const live = numberValue(values, "live_load_plf");
  const isSteel = calculatorId === "steel_beam_aisc";
  const isLintel = calculatorId === "masonry_lintel_tms";
  const isHeader = calculatorId === "cfs_header";
  const title = isSteel ? "Rolled W-shape beam model" : isLintel ? "Reinforced masonry lintel model" : isHeader ? "Two-member CFS header model" : "CFS joist model";
  const subtitle = isSteel
    ? "Major-axis demand with entered compression-flange unbraced length"
    : isLintel
      ? "Simple span with uniform, symmetric triangular, and midpoint service loads"
      : "Exact SFIA simple-span uniform-load table configuration";
  const loadLabel = isSteel
    ? `Mr ${valueLabel(numberValue(values, "required_moment_kipft"), "kip-ft")} · Vr ${valueLabel(numberValue(values, "required_shear_kip"), "kip")}`
    : isLintel
      ? `w ${valueLabel(numberValue(values, "uniform_load_plf"), "plf")}`
      : `D ${valueLabel(dead, "plf")} · L ${valueLabel(live, "plf")}`;
  return (
    <SvgFrame title={title} subtitle={subtitle} className="calc-schematic-card--beam">
      <ArrowDefs id={marker} />
      <UniformLoad marker={marker} />
      {isLintel && numberValue(values, "triangular_peak_load_plf") > 0 ? (
        <path d="M58 83 L188 45 L318 83" className="calc-schematic__load calc-schematic__load--secondary" />
      ) : null}
      {isLintel && numberValue(values, "midpoint_load_lb") > 0 ? (
        <line x1="188" y1="22" x2="188" y2="82" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
      ) : null}
      <rect x="48" y="86" width="280" height={isLintel ? 34 : 18} className={isLintel ? "calc-schematic__masonry" : "calc-schematic__member"} />
      <Support x={62} y={120} />
      <Support x={314} y={120} />
      <Dimension x1={62} y1={164} x2={314} y2={164} label={valueLabel(span, "ft")} />
      <text x="188" y="34" textAnchor="middle" className="calc-schematic__label">{loadLabel}</text>
      {isSteel ? (
        <g transform="translate(344 67)" className="calc-schematic__section-inset">
          <path d="M0 0h54v9H32v54h22v9H0v-9h22V9H0z" />
          <text x="27" y="89" textAnchor="middle">{textValue(values, "steel_shape_key", "W-shape")}</text>
          <text x="27" y="102" textAnchor="middle">Lb {valueLabel(numberValue(values, "unbraced_length_ft"), "ft")}</text>
        </g>
      ) : isHeader ? (
        <g transform="translate(348 74)" className="calc-schematic__section-inset">
          <path d="M0 0h18v60H0M42 0H24v60h18" />
          <text x="21" y="78" textAnchor="middle">2 members</text>
        </g>
      ) : null}
    </SvgFrame>
  );
}

function SteelColumnSchematic({ values, marker }: { values: CalculatorSchematicValues; marker: string }) {
  return (
    <SvgFrame title="Rolled W-shape column model" subtitle="Entered effective lengths with axial and fully braced biaxial moment demands">
      <ArrowDefs id={marker} />
      <line x1="188" y1="39" x2="188" y2="174" className="calc-schematic__member calc-schematic__member--line" />
      <rect x="155" y="170" width="66" height="10" className="calc-schematic__support-line" />
      <line x1="188" y1="12" x2="188" y2="54" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
      <path d="M147 67c-26 27-25 69 2 93" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
      <Dimension x1={250} y1={40} x2={250} y2={174} label={`Lx ${valueLabel(numberValue(values, "lx_ft"), "ft")}`} />
      <Dimension x1={314} y1={40} x2={314} y2={174} label={`Ly ${valueLabel(numberValue(values, "ly_ft"), "ft")}`} />
      <text x="188" y="14" textAnchor="middle" className="calc-schematic__label">Pr {valueLabel(numberValue(values, "required_axial_kip"), "kip")}</text>
      <text x="94" y="92" textAnchor="middle" className="calc-schematic__label">Mx {valueLabel(numberValue(values, "required_mx_kipft"), "kip-ft")}</text>
      <g transform="translate(28 126)" className="calc-schematic__section-inset">
        <path d="M0 0h62v9H37v54h25v9H0v-9h25V9H0z" />
        <text x="31" y="89" textAnchor="middle">{textValue(values, "steel_shape_key", "W-shape")}</text>
      </g>
      <text x="278" y="204" textAnchor="middle" className="calc-schematic__label">Kx {numberValue(values, "kx", 1).toLocaleString()} · Ky {numberValue(values, "ky", 1).toLocaleString()}</text>
    </SvgFrame>
  );
}

function StudWallSchematic({ values, marker }: { values: CalculatorSchematicValues; marker: string }) {
  const spacing = numberValue(values, "stud_spacing_in", 16);
  return (
    <SvgFrame title="CFS stud-wall model" subtitle="Exact SFIA height, spacing, pressure, and axial-load matrix row">
      <ArrowDefs id={marker} />
      <rect x="78" y="40" width="210" height="142" className="calc-schematic__outline" />
      {[98, 140, 182, 224, 266].map((x) => <line key={x} x1={x} y1="40" x2={x} y2="182" className="calc-schematic__member calc-schematic__member--line" />)}
      {[70, 91, 112, 133, 154].map((y) => <line key={y} x1="318" y1={y} x2="280" y2={y} className="calc-schematic__load" markerEnd={`url(#${marker})`} />)}
      <line x1="182" y1="12" x2="182" y2="46" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
      <Dimension x1={54} y1={40} x2={54} y2={182} label={valueLabel(numberValue(values, "wall_height_ft"), "ft")} />
      <Dimension x1={98} y1={208} x2={140} y2={208} label={`${spacing} in o.c.`} />
      <text x="348" y="58" textAnchor="middle" className="calc-schematic__label">p {valueLabel(numberValue(values, "lateral_pressure_psf"), "psf")}</text>
      <text x="182" y="15" textAnchor="middle" className="calc-schematic__label">P {valueLabel(numberValue(values, "axial_load_kip"), "kip/stud")}</text>
    </SvgFrame>
  );
}

function ConcreteBeamSchematic({ values, marker }: { values: CalculatorSchematicValues; marker: string }) {
  const count = Math.max(1, Math.min(8, Math.round(numberValue(values, "tension_bar_count", 4))));
  return (
    <SvgFrame title="Rectangular reinforced-concrete beam" subtitle="Single tension-bar layer with closed vertical stirrups">
      <ArrowDefs id={marker} />
      <rect x="122" y="28" width="150" height="168" rx="2" className="calc-schematic__concrete" />
      <rect x="139" y="45" width="116" height="134" rx="4" className="calc-schematic__stirrup" />
      {Array.from({ length: count }, (_, index) => {
        const x = count === 1 ? 197 : 151 + index * 92 / (count - 1);
        return <circle key={x} cx={x} cy="163" r="6" className="calc-schematic__rebar" />;
      })}
      <Dimension x1={122} y1={216} x2={272} y2={216} label={`b ${valueLabel(numberValue(values, "width_in"), "in")}`} />
      <Dimension x1={302} y1={28} x2={302} y2={196} label={`h ${valueLabel(numberValue(values, "depth_in"), "in")}`} />
      <text x="72" y="96" textAnchor="middle" className="calc-schematic__label">Mu {valueLabel(numberValue(values, "factored_moment_kipft"), "kip-ft")}</text>
      <text x="72" y="116" textAnchor="middle" className="calc-schematic__label">Vu {valueLabel(numberValue(values, "factored_shear_kip"), "kip")}</text>
      <text x="197" y="19" textAnchor="middle" className="calc-schematic__label">{count} tension bars · s {valueLabel(numberValue(values, "stirrup_spacing_in"), "in")}</text>
    </SvgFrame>
  );
}

function FoundationSchematic({ calculatorId, values, marker }: Props & { marker: string }) {
  const pad = calculatorId === "pad_footing";
  const width = numberValue(values, "footing_width_ft", 4);
  const length = numberValue(values, "footing_length_ft", width);
  const thickness = numberValue(values, "thickness_in", 18);
  return (
    <SvgFrame
      title={pad ? "Concentric interior pad footing" : "Continuous wall footing strip"}
      subtitle={pad ? "Uniform bearing with one-way shear, punching, flexure, and column bearing" : "One-foot transverse strip with uniform bearing, shear, and flexure"}
    >
      <ArrowDefs id={marker} />
      <rect x="72" y="111" width="265" height="62" className="calc-schematic__concrete" />
      <rect x={pad ? 172 : 150} y="48" width={pad ? 64 : 108} height="63" className="calc-schematic__concrete calc-schematic__concrete--column" />
      <line x1="204" y1="16" x2="204" y2="59" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
      {[96, 132, 168, 204, 240, 276, 312].map((x) => <line key={x} x1={x} y1="205" x2={x} y2="177" className="calc-schematic__soil" markerEnd={`url(#${marker})`} />)}
      <line x1="92" y1="157" x2="318" y2="157" className="calc-schematic__rebar-line" />
      <Dimension x1={72} y1={218} x2={337} y2={218} label={`${pad ? `L ${valueLabel(length, "ft")} · ` : ""}B ${valueLabel(width, "ft")}`} />
      <Dimension x1={365} y1={111} x2={365} y2={173} label={`h ${valueLabel(thickness, "in")}`} />
      <text x="204" y="16" textAnchor="middle" className="calc-schematic__label">D {valueLabel(numberValue(values, pad ? "dead_load_kip" : "dead_line_load_kipft"), pad ? "kip" : "kip/ft")}</text>
      <text x="48" y="198" className="calc-schematic__label">q soil</text>
    </SvgFrame>
  );
}

function MasonryWallSchematic({ calculatorId, values, marker }: Props & { marker: string }) {
  const shearWall = calculatorId === "masonry_shear_wall_tms";
  const title = shearWall ? "Reinforced masonry shear wall" : "Out-of-plane masonry wall strip";
  const subtitle = shearWall
    ? "Exact published wall geometry and reinforcement pattern; axial-flexure, shear, and base friction"
    : "No-axial-load simple span between top and bottom supports";
  return (
    <SvgFrame title={title} subtitle={subtitle}>
      <ArrowDefs id={marker} />
      <rect
        x="118"
        y="35"
        width={shearWall ? 178 : 104}
        height="153"
        className="calc-schematic__masonry"
        style={{ fill: `url(#${marker}-masonry)` }}
      />
      {shearWall ? (
        <>
          {[136, 172, 208, 244, 280].map((x) => <line key={x} x1={x} y1="43" x2={x} y2="181" className="calc-schematic__rebar-line" />)}
          <line x1="88" y1="88" x2="128" y2="88" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
          <line x1="207" y1="9" x2="207" y2="46" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
          <path d="M324 50c28 31 28 78 0 109" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
          <Dimension x1={118} y1={214} x2={296} y2={214} label={valueLabel(numberValue(values, "wall_length_ft"), "ft")} />
          <text x="60" y="83" className="calc-schematic__label">Vu {valueLabel(numberValue(values, "required_in_plane_shear_kip"), "kip")}</text>
          <text x="207" y="12" textAnchor="middle" className="calc-schematic__label">Pu {valueLabel(numberValue(values, "required_axial_kip"), "kip")}</text>
          <text x="355" y="112" textAnchor="middle" className="calc-schematic__label">Mu</text>
        </>
      ) : (
        <>
          {[66, 94, 122, 150].map((y) => <line key={y} x1="286" y1={y} x2="218" y2={y} className="calc-schematic__load" markerEnd={`url(#${marker})`} />)}
          <line x1="103" y1="35" x2="237" y2="35" className="calc-schematic__support-line" />
          <line x1="103" y1="188" x2="237" y2="188" className="calc-schematic__support-line" />
          <text x="326" y="108" textAnchor="middle" className="calc-schematic__label">p {valueLabel(numberValue(values, "lateral_pressure_psf"), "psf")}</text>
        </>
      )}
      <Dimension x1={82} y1={35} x2={82} y2={188} label={valueLabel(numberValue(values, "wall_height_ft"), "ft")} />
    </SvgFrame>
  );
}

function HangerSchematic({ values, marker }: { values: CalculatorSchematicValues; marker: string }) {
  return (
    <SvgFrame title="Proprietary hanger installation" subtitle="Independent directional demands against the exact published fastener and member configuration">
      <ArrowDefs id={marker} />
      <rect x="60" y="36" width="96" height="164" className="calc-schematic__wood" />
      <rect x="156" y="90" width="190" height="70" className="calc-schematic__wood calc-schematic__wood--second" />
      <path d="M144 80v94h34v-18h-16V98h16V80z" className="calc-schematic__hardware" />
      {[105, 126, 147].map((y) => <circle key={y} cx="151" cy={y} r="4" className="calc-schematic__fastener" />)}
      <line x1="250" y1="174" x2="250" y2="214" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
      <line x1="290" y1="78" x2="290" y2="42" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
      <text x="250" y="222" textAnchor="middle" className="calc-schematic__label">download {valueLabel(numberValue(values, "download_demand_lb"), "lb")}</text>
      <text x="290" y="34" textAnchor="middle" className="calc-schematic__label">uplift {valueLabel(numberValue(values, "uplift_demand_lb"), "lb")}</text>
    </SvgFrame>
  );
}

function HoldDownSchematic({ values, marker }: { values: CalculatorSchematicValues; marker: string }) {
  return (
    <SvgFrame title="Hold-down series load path" subtitle="Device, threaded rod, wood-post compression, and foundation anchorage must all pass">
      <ArrowDefs id={marker} />
      <rect x="72" y="30" width="86" height="128" className="calc-schematic__wood" />
      <rect x="46" y="158" width="258" height="45" className="calc-schematic__concrete" />
      <path d="M150 98h42v70h-22v-49h-20z" className="calc-schematic__hardware" />
      <line x1="181" y1="116" x2="181" y2="190" className="calc-schematic__rod" />
      <circle cx="181" cy="190" r="8" className="calc-schematic__fastener" />
      <line x1="115" y1="78" x2="115" y2="21" className="calc-schematic__load" markerEnd={`url(#${marker})`} />
      <text x="115" y="16" textAnchor="middle" className="calc-schematic__label">T {valueLabel(numberValue(values, "tension_demand_lb"), "lb")}</text>
      <g className="calc-schematic__load-path-list">
        <text x="324" y="69">1 device</text>
        <text x="324" y="91">2 rod</text>
        <text x="324" y="113">3 post</text>
        <text x="324" y="135">4 anchorage</text>
      </g>
    </SvgFrame>
  );
}

function SectionSchematic({ values }: { values: CalculatorSchematicValues }) {
  const shape = textValue(values, "shape_type", "rectangle");
  const shapeNode = (() => {
    if (shape === "solid_circle") return <circle cx="205" cy="106" r="64" className="calc-schematic__section-shape" />;
    if (shape === "hollow_circle") return <><circle cx="205" cy="106" r="64" className="calc-schematic__section-shape" /><circle cx="205" cy="106" r="46" className="calc-schematic__section-void" /></>;
    if (shape === "hollow_rectangle") return <><rect x="132" y="39" width="146" height="134" className="calc-schematic__section-shape" /><rect x="151" y="58" width="108" height="96" className="calc-schematic__section-void" /></>;
    if (shape === "i_section") return <path d="M132 38h146v20h-61v96h61v20H132v-20h61V58h-61z" className="calc-schematic__section-shape" />;
    return <rect x="132" y="39" width="146" height="134" className="calc-schematic__section-shape" />;
  })();
  const dimension = shape.includes("circle")
    ? `D ${valueLabel(numberValue(values, "diameter_in"), "in")}`
    : shape === "i_section"
      ? `bf ${valueLabel(numberValue(values, "flange_width_in"), "in")} · d ${valueLabel(numberValue(values, "depth_in"), "in")}`
      : `b ${valueLabel(numberValue(values, "width_in"), "in")} · h ${valueLabel(numberValue(values, "depth_in"), "in")}`;
  return (
    <SvgFrame title="Symmetric parametric section" subtitle="Centroidal area, second moments, section moduli, and radii of gyration">
      {shapeNode}
      <line x1="104" y1="106" x2="306" y2="106" className="calc-schematic__axis" />
      <line x1="205" y1="14" x2="205" y2="197" className="calc-schematic__axis" />
      <text x="309" y="102" className="calc-schematic__axis-label">x</text>
      <text x="211" y="20" className="calc-schematic__axis-label">y</text>
      <circle cx="205" cy="106" r="3" className="calc-schematic__centroid" />
      <text x="205" y="220" textAnchor="middle" className="calc-schematic__label">{dimension}</text>
    </SvgFrame>
  );
}

export const SCHEMATIC_CALCULATOR_IDS = new Set([
  "steel_beam_aisc",
  "steel_column_aisc",
  "cfs_joist",
  "cfs_stud_wall",
  "cfs_header",
  "concrete_beam_aci",
  "continuous_footing",
  "pad_footing",
  "masonry_bearing_wall_tms",
  "masonry_shear_wall_tms",
  "masonry_lintel_tms",
  "proprietary_hanger_connection",
  "proprietary_hold_down_connection",
  "section_properties",
]);

export function CalculatorSchematic({ calculatorId, values, className }: Props) {
  const marker = `calc-arrow-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  if (!SCHEMATIC_CALCULATOR_IDS.has(calculatorId)) return null;
  if (["steel_beam_aisc", "cfs_joist", "cfs_header", "masonry_lintel_tms"].includes(calculatorId)) {
    return <BeamSchematic calculatorId={calculatorId} values={values} className={className} marker={marker} />;
  }
  if (calculatorId === "steel_column_aisc") return <SteelColumnSchematic values={values} marker={marker} />;
  if (calculatorId === "cfs_stud_wall") return <StudWallSchematic values={values} marker={marker} />;
  if (calculatorId === "concrete_beam_aci") return <ConcreteBeamSchematic values={values} marker={marker} />;
  if (["continuous_footing", "pad_footing"].includes(calculatorId)) {
    return <FoundationSchematic calculatorId={calculatorId} values={values} className={className} marker={marker} />;
  }
  if (["masonry_bearing_wall_tms", "masonry_shear_wall_tms"].includes(calculatorId)) {
    return <MasonryWallSchematic calculatorId={calculatorId} values={values} className={className} marker={marker} />;
  }
  if (calculatorId === "proprietary_hanger_connection") return <HangerSchematic values={values} marker={marker} />;
  if (calculatorId === "proprietary_hold_down_connection") return <HoldDownSchematic values={values} marker={marker} />;
  return <SectionSchematic values={values} />;
}
